// =============================================================================
// BP-07-03: ResourceCategoryPage
// Category-filtered resource view
// =============================================================================

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SlidersHorizontal } from 'lucide-react';
import { useCategoryResources, useResourceCategory } from '@/features/resources/hooks/useResourceCategories';
import { useResourceStore, useFiltersAsParams, useActiveFilterCount } from '@/features/resources/stores/resourceStore';
import {
  ResourceGridWithLoadMore,
  ResourceBreadcrumb,
  CategoryNav,
  ResourceFilters,
  FiltersModal,
  ActiveFiltersTags,
} from '../components';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ResourceCategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  // Fetch category details
  const { data: category, isLoading: isCategoryLoading } = useResourceCategory(slug!);

  // Get filters from store
  const filters = useFiltersAsParams();
  const activeFilterCount = useActiveFilterCount();
  const { clearFilters } = useResourceStore();

  // Fetch category resources
  const {
    data,
    isLoading: isResourcesLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCategoryResources(slug!, { ...filters, limit: 12 });

  // Flatten pages into resources array
  const resources = data?.pages.flatMap((page) => page.resources) ?? [];
  const isLoading = isCategoryLoading || isResourcesLoading;

  // 404 state
  if (!isCategoryLoading && !category) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-3xl font-semibold text-navy mb-4">
            Category Not Found
          </h1>
          <p className="text-warm-gray mb-6">
            The category you're looking for doesn't exist or has been moved.
          </p>
          <Link
            to="/resources"
            className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors"
          >
            Browse All Resources
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{category?.name || 'Category'} Resources | BrightPath</title>
        <meta
          name="description"
          content={category?.description || `Browse resources in the ${category?.name} category.`}
        />
      </Helmet>

      <div className="min-h-screen bg-cream">
        {/* Header */}
        <section className="bg-gradient-to-b from-cream to-cream-dark py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <ResourceBreadcrumb category={category || undefined} className="mb-6" />

            {/* Category Info */}
            <div className="max-w-3xl">
              <h1 className="font-display text-3xl md:text-4xl font-medium text-navy mb-3">
                {category?.name || 'Loading...'}
              </h1>
              {category?.description && (
                <p className="text-lg text-warm-gray">{category.description}</p>
              )}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="border-b border-warm-gray/10 bg-white sticky top-16 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <CategoryNav variant="horizontal" selectedSlug={slug} showCount={false} />
          </div>
        </section>

        {/* Main Content */}
        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-8">
              {/* Sidebar Filters (desktop) */}
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-36">
                  <ResourceFilters
                    variant="sidebar"
                    hiddenFilters={['category']} // Hide category filter since we're in a category
                  />
                </div>
              </aside>

              {/* Main Content Area */}
              <div className="flex-1 min-w-0">
                {/* Mobile Filter Button */}
                <div className="lg:hidden mb-6">
                  <button
                    type="button"
                    onClick={() => setShowFiltersModal(true)}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2.5 bg-white border border-warm-gray/20 rounded-lg transition-colors',
                      activeFilterCount > 0 && 'border-sage'
                    )}
                  >
                    <SlidersHorizontal className="w-5 h-5 text-warm-gray" />
                    <span className="font-medium text-navy">Filters</span>
                    {activeFilterCount > 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-sage text-white rounded-full">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>
                </div>

                {/* Active Filter Tags */}
                {activeFilterCount > 0 && (
                  <div className="mb-6">
                    <ActiveFiltersTags />
                  </div>
                )}

                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-display text-xl font-semibold text-navy">
                      {activeFilterCount > 0 ? 'Filtered Results' : 'All Resources'}
                    </h2>
                    {data?.pages[0]?.total !== undefined && (
                      <p className="text-sm text-warm-gray mt-0.5">
                        {data.pages[0].total.toLocaleString()} resources in{' '}
                        {category?.name}
                      </p>
                    )}
                  </div>
                </div>

                {/* Resources Grid */}
                <ResourceGridWithLoadMore
                  resources={resources}
                  isLoading={isLoading}
                  isFetchingNextPage={isFetchingNextPage}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  emptyMessage={
                    activeFilterCount > 0
                      ? 'No resources match your filters in this category.'
                      : `No resources in ${category?.name || 'this category'} yet.`
                  }
                  emptyAction={
                    activeFilterCount > 0
                      ? { label: 'Clear Filters', onClick: clearFilters }
                      : { label: 'Browse All Resources', href: '/resources' }
                  }
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Filters Modal (mobile) */}
      <FiltersModal
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        hiddenFilters={['category']}
      />
    </>
  );
}

export default ResourceCategoryPage;


